import {
  FluidFormElementTranslationOptions,
  FluidOptionButtonType,
  FluidSize,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { FluidIconPosition } from '../../../model/functional/fluid-icon-position.enum';
import {
  TranslatableFluidOption,
  translateOption,
} from '../../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { EventUtils } from '../../../utils/events/event-utils';
import { themeVar } from '../../../utils/themes/theme.variables';

interface ButtonGroupProps extends FluidFunctionalComponentProps {
  host: any;
  option: TranslatableFluidOption;
  optionButtonType: FluidOptionButtonType;
  iconPosition?: FluidIconPosition;
  i18nStore?: FluidI18nStore;
  fluidTranslate?: FluidFormElementTranslationOptions;
  identifier: string;
}

// ================================================================ //
// -- Public Functional Components

/**
 * ButtonGroupOption
 */
export const ButtonGroupOption: FunctionalComponent<ButtonGroupProps> = (
  props: ButtonGroupProps
) => {
  const {
    host,
    option,
    optionButtonType,
    i18nStore,
    fluidTranslate,
    identifier,
  } = props;

  return (
    <label
      role='button'
      title={option.tooltip}
      tabIndex={host.disabled || option.disabled ? -1 : 0}
      aria-pressed={String(option.selected)}
      aria-disabled={String(!!host.disabled || !!option.disabled)}
      onClick={(e: MouseEvent) => host.debounceClick(option, e)}
      onKeyDown={(e: KeyboardEvent) => handleKeyDown(e)}
      data-test-id={`${identifier}-option-btn-${option.label}`}
      class={{
        'button-group-option': true,
        'tile-button': optionButtonType === FluidOptionButtonType.TILE,
        'option-selected': option.selected,
        'option-disabled': host.disabled || option.disabled,
      }}>
      <ButtonGroupInput {...props} />
      <ButtonGroupIcon iconPosition={FluidIconPosition.BEFORE} {...props} />
      <span
        class={'label-text'}
        data-test-id={`${identifier}-text-${option.label}`}>
        {translateOption(option, i18nStore, fluidTranslate)}
      </span>
      <ButtonGroupIcon iconPosition={FluidIconPosition.AFTER} {...props} />
    </label>
  );
};

// ================================================================ //
// -- Private Functional Components

/**
 * ButtonGroupInput
 */
const ButtonGroupInput: FunctionalComponent<ButtonGroupProps> = (
  props: ButtonGroupProps
) => {
  const { host, option, identifier } = props;

  return (
    <input
      type='radio'
      class='option-input'
      data-test-id={`${identifier}-option-input-${option.label}-radio`}
      name={host._name}
      value={option.value}
      checked={option.selected}
      disabled={host.disabled || option.disabled}
      onChange={() => host.handleOptionChange(option)}
    />
  );
};

/**
 * ButtonGroupIcon
 */
const ButtonGroupIcon: FunctionalComponent<ButtonGroupProps> = (
  props: ButtonGroupProps
) => {
  const { host, option, iconPosition, identifier } = props;

  const iconConfig =
    iconPosition === FluidIconPosition.BEFORE
      ? option.iconBefore
      : option.iconAfter;

  if (
    isNullOrUndefined(iconConfig) &&
    (iconPosition === 'before' || isNullOrUndefined(option.icon))
  )
    return;

  const defaultSize = themeVar<FluidSize>(host.theme, 'icons.default_size');

  return (
    <fluid-icon
      style={!!option.iconColor && { color: option.iconColor }}
      class={{
        [`option-icon icon-${iconPosition}`]: true,
        [`severity-icon icon-${option.icon}`]: !!option.icon,
        'color-override': !!option.iconColor,
      }}
      data-test-id={`${identifier}-option-icon`}
      config={{
        size: defaultSize,
        key: iconPosition === 'after' && option.icon, // support for deprecated "icon" property
        ...iconConfig, // config merged here so that default values for "size" & "key" can be overridden
        color: undefined, // NEEDS TO BE UNDEFINED - allows color change for :hover & selected states
        theme: host.theme,
        overrideGlobalTheme: true,
      }}>
      {!!iconConfig?.slotName && (
        <slot name={iconConfig.slotName} slot={iconConfig.slotName} />
      )}
    </fluid-icon>
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

const handleKeyDown = (event: KeyboardEvent): void => {
  EventUtils.arrowToTab(event);
  EventUtils.keySelect(event);
};
